/*
 * <search-overlay> — full-screen predictive search (#172).
 * Markup: snippets/search-overlay.liquid. Results: sections/search-predictivesearch.liquid.
 *
 * Any element with [data-search-overlay-open] opens the overlay (click, or focus on
 * an input inside it). Cmd/Ctrl+K toggles it. Results come from Shopify's
 * Predictive Search API, debounced, abortable and cached per term.
 *
 * Fetch/abort/cache and single-result behaviour follow Shopify Horizon's
 * predictive-search.js.
 * Copyright (c) 2025-present Shopify Inc. Used under the Horizon theme licence.
 *
 * Events (bubbling): search-overlay:open, search-overlay:close
 */
const RECENT_KEY = 'search-overlay:recent';
const RECENT_MAX = 6;
const DEBOUNCE_MS = 200;
const OPEN_SELECTOR = '[data-search-overlay-open]';

class SearchOverlay extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.form = this.querySelector('form');
    this.input = this.querySelector('[data-input]');
    this.clearBtn = this.querySelector('[data-clear]');
    this.live = this.querySelector('[data-live]');
    this.defaultEl = this.querySelector('[data-default]');
    this.resultsEl = this.querySelector('[data-results]');
    this.bodyEl = this.querySelector('[data-body]');
    this.recentGroup = this.querySelector('[data-recent]');
    this.recentList = this.querySelector('[data-recent-list]');
    if (!this.dialog || !this.input) return;

    this.cache = new Map();
    this.controller = null;
    this.timer = 0;
    this.selected = -1;
    this.renderedTerm = '';
    this.restoringFocus = false;

    this.onDocClick = this.onDocClick.bind(this);
    this.onDocFocusIn = this.onDocFocusIn.bind(this);
    this.onDocKeydown = this.onDocKeydown.bind(this);
    this.onInput = this.onInput.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onDialogClick = this.onDialogClick.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.onCancel = this.onCancel.bind(this);
    this.onClose = this.onClose.bind(this);
    this.onResize = () => {
      if (this.anchored && this.headerInput) this.placeUnder(this.headerInput);
      else if (this.nonModal && this.dialog.open) this.syncMobileTop();
    };

    // Capture phase so the overlay wins before Kalles' delegated drawer handlers.
    document.addEventListener('click', this.onDocClick, true);
    document.addEventListener('focusin', this.onDocFocusIn);
    document.addEventListener('keydown', this.onDocKeydown);
    this.input.addEventListener('input', this.onInput);
    this.dialog.addEventListener('keydown', this.onKeydown);
    this.dialog.addEventListener('click', this.onDialogClick);
    this.dialog.addEventListener('cancel', this.onCancel);
    this.dialog.addEventListener('close', this.onClose);
    this.form.addEventListener('submit', this.onSubmit);

    this.renderRecent();
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocClick, true);
    document.removeEventListener('focusin', this.onDocFocusIn);
    document.removeEventListener('keydown', this.onDocKeydown);
    this.input?.removeEventListener('input', this.onInput);
    this.dialog?.removeEventListener('keydown', this.onKeydown);
    this.dialog?.removeEventListener('click', this.onDialogClick);
    this.dialog?.removeEventListener('cancel', this.onCancel);
    this.dialog?.removeEventListener('close', this.onClose);
    this.form?.removeEventListener('submit', this.onSubmit);
    window.removeEventListener('resize', this.onResize);
    this.headerObserver?.disconnect();
    clearTimeout(this.timer);
    this.controller?.abort();
  }

  /* ---------- open / close ---------- */

  open(trigger) {
    if (this.dialog.open) return;
    this.closeThemeDrawer();

    const source = trigger?.matches?.('input') ? trigger : trigger?.querySelector?.('input[name="q"], input[type="text"], input[type="search"]');
    const seed = source?.value?.trim() || '';

    this.syncBarTheme();
    /* Below the desktop breakpoint the overlay sits under the header rather
       than over it, so the cart and the menu stay reachable while searching. A
       modal dialog makes everything outside it inert, which would leave those
       controls visible but dead, so open non-modally there. The cost is that
       the browser no longer handles Escape or the backdrop for us: onKeydown
       covers Escape, onDocClick covers tapping outside, and the CSS draws the
       dim layer a ::backdrop would have given us. */
    const mobile = window.matchMedia('(max-width: 1024.98px)').matches;
    /* On desktop, opened from the header's own search field, the results hang
       under that field instead of the overlay laying a second bar over the
       header. The field stays the one input and its X closes search in one
       click. With no field to borrow (a search icon, Cmd+K) the full bar
       below still opens as before. */
    this.anchored = !mobile && !!source;
    this.nonModal = mobile || this.anchored;
    this.toggleAttribute('data-nonmodal', this.nonModal);
    this.toggleAttribute('data-anchored', this.anchored);
    if (this.anchored) {
      this.placeUnder(source);
      window.addEventListener('resize', this.onResize);
    }
    if (mobile) {
      this.syncMobileTop();
      /* Page scroll is locked while search is open, but a resize, a rotation, a
         header that reflows or anything above it changing height (an
         announcement carousel with adaptive height) can still move the header's
         edge, so follow it: the header and every element before it. */
      window.addEventListener('resize', this.onResize);
      const header = this.headerSection();
      if (header && 'ResizeObserver' in window) {
        this.headerObserver ??= new ResizeObserver(this.onResize);
        this.headerObserver.observe(header);
        for (let node = header; node && node !== document.body; node = node.parentElement) {
          for (let before = node.previousElementSibling; before; before = before.previousElementSibling) {
            this.headerObserver.observe(before);
          }
        }
      }
    }
    if (this.nonModal) {
      /* A modal dialog stops the page behind it scrolling; a non-modal one does
         not, and the overlay is pinned under a sticky header whose position
         would drift as that page moved. */
      document.documentElement.classList.add('search-overlay-locked');
      this.dialog.show();
      this.adoptHeaderField(source);
    } else {
      source?.blur();
      this.dialog.showModal();
    }
    this.input.value = seed || this.input.value;
    this.input.focus();
    this.input.select();
    this.syncClear();

    const term = this.input.value.trim();
    if (term) this.search(term);
    else this.showDefault();

    this.dispatchEvent(new CustomEvent('search-overlay:open', { bubbles: true }));
  }

  close() {
    if (!this.dialog.open) return;
    this.restoringFocus = true;
    document.documentElement.classList.remove('search-overlay-locked');
    window.removeEventListener('resize', this.onResize);
    this.headerObserver?.disconnect();
    this.releaseHeaderField();
    this.dialog.close();
  }

  headerSection() {
    return this.closest('.t4s-section-header') || document.querySelector('.t4s-section-header');
  }

  /* With the sticky header off, a small scroll leaves part of the header above
     the screen, so starting the mobile panel at --header-height left the page
     showing between the two: start it at the header's bottom edge as it is now.
     A stuck header sits at --header-height, which the theme keeps current, and
     may be part way through its hide-on-scroll slide, so it keeps that offset. */
  syncMobileTop() {
    const header = this.headerSection();
    if (!header || document.documentElement.classList.contains('is-header--stuck')) {
      this.style.removeProperty('--search-overlay-top');
      return;
    }
    this.style.setProperty('--search-overlay-top', `${Math.max(0, Math.round(header.getBoundingClientRect().bottom))}px`);
  }

  /* Pins the results card under the header field it borrowed: flush with the
     field's right edge, since the field sits at the header's trailing end and
     the card is wider than it. Measured from the form, which draws the
     field's border. Page scroll is locked while search is open, so only a
     resize can move the field. */
  placeUnder(field) {
    const box = (field.closest('form') || field).getBoundingClientRect();
    this.style.setProperty('--search-overlay-anchor-top', `${Math.round(box.bottom + 8)}px`);
    this.style.setProperty('--search-overlay-anchor-right', `${Math.round(document.documentElement.clientWidth - box.right)}px`);
  }

  /** Kalles keeps one drawer open at a time; the mobile menu may be open when search is tapped. */
  closeThemeDrawer() {
    // Kalles sets is--opend-drawer on <body> (seen live on the mobile menu), not on <html>.
    if (!document.body.classList.contains('is--opend-drawer')) return;
    document.querySelector('.t4s-close-overlay')?.click();
  }

  onDocClick(event) {
    const trigger = event.target.closest?.(OPEN_SELECTOR);
    if (!trigger || this.contains(trigger)) {
      /* Open non-modally there is no backdrop to catch a tap outside, so any
         tap beyond the overlay dismisses it. The tap itself is left to run:
         tapping the cart both closes search and follows the link, which is the
         point of keeping the header reachable. */
      if (this.nonModal && this.dialog.open && !this.contains(event.target)) this.close();
      return;
    }
    event.preventDefault();
    /* The search button beside a header field searches for what is typed there
       rather than only opening the overlay; with nothing typed it opens as before. */
    const submit = event.target.closest?.('button[type="submit"], input[type="submit"]');
    const field = submit && trigger.contains(submit) ? submit.form?.querySelector('input[name="q"]') : null;
    if (field?.value.trim()) {
      this.submitFrom(field);
      return;
    }
    this.open(trigger);
  }

  /** Searches for a header field's words through the overlay's form (onSubmit). */
  submitFrom(field) {
    this.input.value = field.value;
    this.form.requestSubmit();
  }

  onDocFocusIn(event) {
    if (this.restoringFocus) return;
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || this.contains(target)) return;
    if (!target.closest(OPEN_SELECTOR)) return;
    this.open(target);
  }

  onDocKeydown(event) {
    if ((event.metaKey || event.ctrlKey) && !event.altKey && event.key.toLowerCase() === 'k') {
      event.preventDefault();
      if (this.dialog.open) this.close();
      else this.open(null);
    }
  }

  onCancel() {
    // Escape: the browser restores focus to the trigger, which must not reopen us.
    this.restoringFocus = true;
  }

  onClose() {
    clearTimeout(this.timer);
    this.controller?.abort();
    this.setLoading(false);
    this.clearSelection();
    // Focus restoration has already happened synchronously inside close().
    setTimeout(() => {
      this.restoringFocus = false;
    }, 0);
    this.dispatchEvent(new CustomEvent('search-overlay:close', { bubbles: true }));
  }

  /* ---------- searching ---------- */

  onInput() {
    this.syncClear();
    // A result picked for the old words no longer answers the new ones (#601).
    this.clearSelection();
    clearTimeout(this.timer);
    const term = this.input.value.trim();
    if (!term) {
      this.controller?.abort();
      this.setLoading(false);
      this.showDefault();
      return;
    }
    this.timer = setTimeout(() => this.search(term), DEBOUNCE_MS);
  }

  async search(term) {
    if (this.cache.has(term)) {
      this.renderResults(term, this.cache.get(term));
      return;
    }

    this.controller?.abort();
    const controller = new AbortController();
    this.controller = controller;
    this.setLoading(true);

    const url = new URL(this.dataset.url, window.location.origin);
    url.searchParams.set('q', term);
    url.searchParams.set('section_id', 'search-predictivesearch');
    url.searchParams.set('resources[type]', 'query,product,collection,page');
    url.searchParams.set('resources[limit]', '10');
    url.searchParams.set('resources[limit_scope]', 'each');
    if (this.dataset.unavailable) url.searchParams.set('resources[options][unavailable_products]', this.dataset.unavailable);

    try {
      const response = await fetch(url, { signal: controller.signal });
      if (!response.ok) throw new Error(`Predictive search returned ${response.status}`);
      const text = await response.text();
      const doc = new DOMParser().parseFromString(text, 'text/html');
      const html = doc.querySelector('.search-overlay__results')?.outerHTML || '';
      this.cache.set(term, html);
      if (this.input.value.trim() === term) this.renderResults(term, html);
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error('[search-overlay]', error);
      // Leave the last good results in place; Enter still submits to /search.
    } finally {
      if (this.controller === controller) this.setLoading(false);
    }
  }

  renderResults(term, html) {
    this.resultsEl.innerHTML = html;
    this.resultsEl.hidden = false;
    this.defaultEl.hidden = true;
    if (this.bodyEl) this.bodyEl.hidden = false;
    this.renderedTerm = term;
    this.clearSelection();
    const status = this.resultsEl.querySelector('template[data-status]');
    this.live.textContent = status ? status.content.textContent.trim() : '';
  }

  showDefault() {
    this.resultsEl.hidden = true;
    this.resultsEl.innerHTML = '';
    this.defaultEl.hidden = false;
    this.renderedTerm = '';
    this.live.textContent = '';
    this.clearSelection();
    this.renderRecent();
    /* The card holds recent searches, the trending pills and the suggested
       products, any of which a store may have switched off -- and a first-time
       shopper has no recent searches either. With none of them to show it is an
       empty white panel hanging under the bar before anything has been searched
       for, so keep it out of the layout until it has something in it. */
    const hasContent = !!this.defaultEl.querySelector('.search-overlay__group:not([hidden])');
    this.defaultEl.hidden = !hasContent;
    if (this.bodyEl) this.bodyEl.hidden = !hasContent;
  }

  setLoading(on) {
    this.toggleAttribute('data-busy', on);
  }

  /* Mobile shows the header's own search bar already, so the overlay hides its
     bar and borrows that field rather than stacking a second one beneath it.
     Everything downstream still reads this.input, so the header field is
     mirrored into it on every keystroke instead of the search path being
     rewritten. The field keeps focus, which also keeps the phone keyboard up. */
  adoptHeaderField(source) {
    this.releaseHeaderField();
    if (!source) return;
    this.headerInput = source;
    this.headerClear = source.closest('form')?.querySelector('[data-header-search-clear]') || null;

    this.onHeaderInput = () => {
      this.input.value = this.headerInput.value;
      this.onInput();
      this.syncHeaderClear();
    };
    this.onHeaderKeydown = (event) => {
      /* An input method composing text (Japanese, Chinese) uses Enter and the
         arrow keys for its own candidates, so leave every key to it. */
      if (event.isComposing) return;
      /* Arrow keys and Enter belong to the results list and the overlay's form. */
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }
      /* Enter searches through the overlay's own form, whose submit handler opens a
         chosen result or the single match and otherwise goes to /search. Left to the
         browser, Enter clicks the header form's search button, and onDocClick
         cancels that click because the field sits inside the opener (#601). */
      if (event.key === 'Enter') {
        event.preventDefault();
        this.submitFrom(this.headerInput);
        return;
      }
      /* Move through the results list as onKeydown does for this.input. Handing
         onKeydown a copied event threw (an Event's target has only a getter), so the
         arrow keys did nothing from the header field (#601). */
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        this.select(this.selected + (event.key === 'ArrowDown' ? 1 : -1));
      }
    };
    this.onHeaderClearClick = (event) => {
      event.preventDefault();
      /* One tap ends the search. Clearing the text and closing used to be two
         presses: the first emptied the field and left the recent-searches panel
         sitting there, so the X appeared not to have worked. */
      this.headerInput.value = '';
      this.input.value = '';
      this.close();
    };

    this.headerInput.addEventListener('input', this.onHeaderInput);
    this.headerInput.addEventListener('keydown', this.onHeaderKeydown);
    this.headerClear?.addEventListener('click', this.onHeaderClearClick);
    this.toggleAttribute('data-external-input', true);
    this.syncHeaderClear();
    this.headerInput.focus();
  }

  releaseHeaderField() {
    if (!this.headerInput) return;
    this.headerInput.removeEventListener('input', this.onHeaderInput);
    this.headerInput.removeEventListener('keydown', this.onHeaderKeydown);
    this.headerClear?.removeEventListener('click', this.onHeaderClearClick);
    if (this.headerClear) this.headerClear.hidden = true;
    this.headerInput = null;
    this.headerClear = null;
    this.toggleAttribute('data-external-input', false);
  }

  syncHeaderClear() {
    if (!this.headerClear) return;
    /* Always the same control doing the same thing, so the label never changes:
       it ends the search whether or not anything has been typed. */
    this.headerClear.hidden = false;
    const label = this.headerClear.dataset.labelClose;
    if (label) this.headerClear.setAttribute('aria-label', label);
  }

  /* The desktop bar sits on top of the header while the overlay is open, so it
     takes the header's own colours rather than a colour of its own. Read at
     open time because the header changes colour between its normal, transparent
     and sticky states, and every header section (bottom, inline, sidebar,
     categories, vertical) scopes those colours to .t4s-header__wrapper, which
     this dialog sits outside of -- so the variables cannot simply be inherited.
     A transparent header has no colour worth copying; the stylesheet default
     stands in that case. */
  syncBarTheme() {
    const header = document.querySelector('.t4s-header__wrapper');
    if (!header) return;
    const styles = getComputedStyle(header);
    const bg = styles.backgroundColor;
    const transparent = !bg || bg === 'transparent' || /^rgba\(\s*\d+,\s*\d+,\s*\d+,\s*0\s*\)$/.test(bg);
    if (transparent) {
      this.style.removeProperty('--search-overlay-bar-bg');
      this.style.removeProperty('--search-overlay-bar-color');
      return;
    }
    this.style.setProperty('--search-overlay-bar-bg', bg);
    this.style.setProperty('--search-overlay-bar-color', styles.color);
  }

  syncClear() {
    const hasText = !!this.input.value;
    /* Drives the mobile rule that hides the button while the field is empty,
       because mobile already shows a Cancel button. */
    this.toggleAttribute('data-empty', !hasText);
    if (!this.clearBtn) return;
    this.clearBtn.hidden = false;
    const label = hasText ? this.clearBtn.dataset.labelClear : this.clearBtn.dataset.labelClose;
    if (label) this.clearBtn.setAttribute('aria-label', label);
  }

  /* ---------- keyboard selection ---------- */

  items() {
    return Array.from(this.querySelectorAll('[data-body] [data-item]')).filter((el) => el.getClientRects().length > 0);
  }

  clearSelection() {
    this.querySelector('[data-item].is-selected')?.classList.remove('is-selected');
    this.selected = -1;
  }

  select(index) {
    const items = this.items();
    this.querySelector('[data-item].is-selected')?.classList.remove('is-selected');
    if (!items.length) {
      this.selected = -1;
      return;
    }
    this.selected = (index + items.length) % items.length;
    const item = items[this.selected];
    item.classList.add('is-selected');
    item.scrollIntoView({ block: 'nearest' });
  }

  onKeydown(event) {
    // A type=search input swallows the first Escape to clear its text; close in one press instead.
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    if (event.target !== this.input) return;
    event.preventDefault();
    this.select(this.selected + (event.key === 'ArrowDown' ? 1 : -1));
  }

  onSubmit(event) {
    const term = this.input.value.trim();
    if (!term) {
      event.preventDefault();
      return;
    }
    this.saveRecent(term);

    // Only a result listed for these exact words counts as chosen (#601).
    const chosen = this.selected > -1 && this.renderedTerm === term ? this.items()[this.selected] : null;
    if (chosen) {
      event.preventDefault();
      chosen.click();
      return;
    }

    const single = this.renderedTerm === term ? this.resultsEl.querySelector('[data-single-result-url]') : null;
    if (single?.dataset.singleResultUrl) {
      event.preventDefault();
      window.location.href = single.dataset.singleResultUrl;
    }
    // Otherwise the form submits natively to /search.
  }

  /* ---------- clicks inside the overlay ---------- */

  onDialogClick(event) {
    const target = event.target;
    if (target === this.dialog) {
      this.close();
      return;
    }
    if (target.closest('[data-close]')) {
      this.close();
      return;
    }
    if (target.closest('[data-clear]')) {
      /* Nothing to clear means the button is acting as the close control. */
      if (!this.input.value) {
        this.close();
        return;
      }
      this.input.value = '';
      this.syncClear();
      this.controller?.abort();
      this.setLoading(false);
      this.showDefault();
      this.input.focus();
      return;
    }
    const slideBtn = target.closest('[data-slide]');
    if (slideBtn) {
      const slider = slideBtn.closest('.search-overlay__group')?.querySelector('[data-slider]');
      const card = slider?.firstElementChild;
      if (slider && card) {
        const step = card.getBoundingClientRect().width + parseFloat(getComputedStyle(slider).columnGap || 0);
        slider.scrollBy({ left: Number(slideBtn.dataset.slide) * step * Math.max(1, Math.floor(slider.clientWidth / step)), behavior: 'smooth' });
      }
      return;
    }
    if (target.closest('[data-clear-recent]')) {
      this.writeRecent([]);
      this.renderRecent();
      this.input.focus();
      return;
    }
    const item = target.closest('[data-item]');
    if (item) {
      const term = item.dataset.term ?? this.input.value.trim();
      if (term) this.saveRecent(term);
    }
  }

  /* ---------- recent searches ---------- */

  readRecent() {
    try {
      const list = JSON.parse(window.localStorage.getItem(RECENT_KEY) || '[]');
      return Array.isArray(list) ? list.filter((t) => typeof t === 'string' && t.trim()) : [];
    } catch {
      return [];
    }
  }

  writeRecent(list) {
    try {
      window.localStorage.setItem(RECENT_KEY, JSON.stringify(list));
    } catch {
      /* storage blocked: recent searches are a convenience only */
    }
  }

  saveRecent(term) {
    const lower = term.toLowerCase();
    const list = [term, ...this.readRecent().filter((t) => t.toLowerCase() !== lower)].slice(0, RECENT_MAX);
    this.writeRecent(list);
  }

  searchUrl(term) {
    const params = new URLSearchParams({ q: term, type: 'product', 'options[prefix]': 'last' });
    if (this.dataset.unavailable) params.set('options[unavailable_products]', this.dataset.unavailable);
    return `${this.form.getAttribute('action')}?${params}`;
  }

  renderRecent() {
    if (!this.recentGroup || !this.recentList) return;
    const list = this.readRecent();
    this.recentGroup.hidden = list.length === 0;
    this.recentList.replaceChildren(
      ...list.map((term) => {
        const li = document.createElement('li');
        const a = document.createElement('a');
        a.className = 'search-overlay__link search-overlay__link--recent';
        a.href = this.searchUrl(term);
        a.dataset.item = '';
        a.dataset.term = term;
        a.textContent = term;
        li.append(a);
        return li;
      })
    );
  }
}

if (!customElements.get('search-overlay')) {
  customElements.define('search-overlay', SearchOverlay);
}
